import { styled } from '@mui/material/styles'
import { Box } from '@mui/material'
import {
	colors,
	spacing,
	typography,
	focusVisible,
	breakpoints,
	radius,
	shadows,
	sizing,
} from '@/styles/designTokens'

export const StyledPopoverContent = styled(Box)(({ theme }) => ({
	backgroundColor: colors.surfaceWhite,
	borderRadius: radius.sheet,
	boxShadow: shadows.card,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	// Desktop: 200×164 fixed dimensions per Figma spec (68 header + 96 info)
	width: 200,
	height: 164,
	padding: 0,
	// Tablet/mobile range (361–720px): fixed 360×184, no padding, no border.
	[theme.breakpoints.down('md')]: {
		width: 360,
		maxWidth: '90vw',
		height: 184,
		padding: 0,
		border: 'none',
		borderRadius: radius.sheet,
	},
	[`@media (max-width: ${breakpoints.compact}px)`]: {
		width: '100vw',
		maxWidth: '100vw',
		padding: 0,
		borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	},
}))

export const StyledModalBackdrop = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: spacing.md,
	width: '100%',
	[`@media (max-width: ${breakpoints.compact}px)`]: {
		padding: 0,
		width: '100vw',
	},
}))

export const StyledHeader = styled(Box)(({ theme }) => ({
	position: 'relative',
	borderBottom: `1px solid ${colors.border}`,
	padding: spacing.lg,
	display: 'flex',
	alignItems: 'center',
	gap: spacing.md,
	flexShrink: 0,
	// Fixed height per Figma spec (desktop: 68px, mobile: 65px)
	height: 68,
	[theme.breakpoints.down('md')]: {
		paddingTop: spacing.sm,
		// Clear the absolutely-positioned CloseButton (right offset + icon + its padding)
		// so truncated header text ellipsizes before running underneath it.
		paddingRight: spacing.md + sizing.iconSm + spacing.sm * 2,
		paddingBottom: spacing.lg,
		paddingLeft: spacing.lg,
		height: 65,
	},
}))

export const StyledPhoneNumber = styled('p')(() => ({
	fontWeight: typography.fontWeightBold,
	fontSize: typography.fontSizeLg,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
	margin: 0,
	// Single-line ellipsis: the header has a fixed height, so a long value
	// (e.g. the email fallback) must truncate rather than wrap and clip.
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	fontFeatureSettings: "'liga' off, 'clig' off",
}))

export const StyledInfoSection = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.md,
	padding: spacing.lg,
	flexShrink: 0,
	// Fixed height per Figma spec — assumes standard name and menu item lengths
	height: 96,
	[theme.breakpoints.down('md')]: {
		padding: spacing.xl,
		height: 112,
	},
}))

export const StyledMenuItem = styled('button')(() => ({
	display: 'flex',
	gap: spacing.xs,
	alignItems: 'center',
	border: 'none',
	backgroundColor: 'transparent',
	cursor: 'pointer',
	fontFamily: typography.fontFamily,
	fontSize: typography.fontSizeMd,
	lineHeight: typography.lineHeightMd,
	fontWeight: typography.fontWeightRegular,
	color: colors.textSecondary,
	padding: 0,
	minHeight: 0,
	textAlign: 'left',
	'&:hover': {
		color: colors.textPrimary,
	},
	'&:focus-visible': {
		...focusVisible,
	},
}))

export const StyledMenuIcon = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: sizing.iconSm,
	height: sizing.iconSm,
	flexShrink: 0,
}))

export const StyledDivider = styled(Box)(() => ({
	height: '1px',
	backgroundColor: colors.divider,
	margin: `${spacing.sm}px 0`,
}))

export const CloseButton = styled('button')(() => ({
	position: 'absolute',
	top: spacing.md,
	right: spacing.md,
	background: 'none',
	border: 'none',
	padding: spacing.sm,
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: colors.textSecondary,
	'&:hover': {
		color: colors.textPrimary,
	},
	'&:focus-visible': {
		...focusVisible,
	},
}))
